import React, { useEffect, useState, createContext, useContext } from 'react';
/**
 * Theme Context Provider
 * Manages theme state, language, accessibility and provides translation function
 */
import { ThemeName, Language, ThemeContextValue, ThemeTokens } from '../types/theme';
import { themeTokens } from '../constants/themeTokens';
// Import translations
import nlCommon from '../../locales/nl/common.json';
import nlApps from '../../locales/nl/apps.json';
import enCommon from '../../locales/en/common.json';
import enApps from '../../locales/en/apps.json';
const translations = {
  nl: {
    ...nlCommon,
    ...nlApps
  },
  en: {
    ...enCommon,
    ...enApps
  }
};
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);
interface ThemeProviderProps {
  children: ReactNode;
}
export function ThemeProvider({
  children
}: ThemeProviderProps) {
  // Load persisted preferences
  const [theme, setThemeState] = useState<ThemeName>(() => {
    const saved = localStorage.getItem('mvl-theme');
    return saved as ThemeName || 'dark';
  });
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('mvl-language');
    return saved as Language || 'nl';
  });
  const [reduceMotion, setReduceMotionState] = useState(() => {
    const saved = localStorage.getItem('mvl-reduce-motion');
    return saved === 'true';
  });
  const [background, setBackgroundState] = useState(() => {
    const saved = localStorage.getItem('mvl-background');
    return saved || 'bg-gradient-to-br from-white to-gray-100';
  });
  // Persist theme changes
  const setTheme = (newTheme: ThemeName) => {
    setThemeState(newTheme);
    localStorage.setItem('mvl-theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('mvl-language', lang);
  };
  const setReduceMotion = (value: boolean) => {
    setReduceMotionState(value);
    localStorage.setItem('mvl-reduce-motion', String(value));
  };
  const setBackground = (gradient: string) => {
    setBackgroundState(gradient);
    localStorage.setItem('mvl-background', gradient);
  };
  // Get current theme tokens
  const getThemeClasses = (): ThemeTokens => {
    if (theme === 'light') {
      return {
        ...themeTokens.light,
        bg: background // Use custom background for light theme
      };
    }
    return themeTokens[theme];
  };
  // Translation function with nested key support
  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[language];
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }
    return typeof value === 'string' ? value : key;
  };
  // Initialize theme on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, []);
  const value: ThemeContextValue = {
    theme,
    setTheme,
    language,
    setLanguage,
    reduceMotion,
    setReduceMotion,
    background,
    setBackground,
    getThemeClasses,
    t
  };
  return <ThemeContext.Provider value={value} data-id="element-981">{children}</ThemeContext.Provider>;
}
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}